// Fetch wrapper for the Round Recon API.
//
// Every endpoint returns the REST-SPEC envelope:
//   { status, code, message, data, timestamp }
// On success we unwrap `data`. On failure we throw an ApiError with
// the envelope's `code`, `message`, and HTTP status attached.

const BASE_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? '/api';

export interface ApiEnvelope<T> {
  status: string;
  code: number;
  message: string;
  data: T;
  timestamp: string;
}

export class ApiError extends Error {
  public readonly httpStatus: number;
  public readonly code: number;
  public readonly serverMessage: string;
  public readonly envelope?: ApiEnvelope<unknown>;

  constructor(
    message: string,
    httpStatus: number,
    code: number,
    serverMessage: string,
    envelope?: ApiEnvelope<unknown>
  ) {
    super(message);
    this.name = 'ApiError';
    this.httpStatus = httpStatus;
    this.code = code;
    this.serverMessage = serverMessage;
    this.envelope = envelope;
  }
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

function buildUrl(path: string): string {
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  const trimmedBase = BASE_URL.replace(/\/$/, '');
  const trimmedPath = path.startsWith('/') ? path : `/${path}`;
  return `${trimmedBase}${trimmedPath}`;
}

async function parseEnvelope<T>(response: Response): Promise<ApiEnvelope<T> | undefined> {
  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) return undefined;
  try {
    return (await response.json()) as ApiEnvelope<T>;
  } catch {
    return undefined;
  }
}

/**
 * Issue a request against the Round Recon API. Returns the unwrapped
 * `data` on success and throws `ApiError` on any non-2xx response or
 * any envelope with a non-zero `code`.
 */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, headers = {}, signal } = options;

  const init: RequestInit = {
    method,
    headers: {
      Accept: 'application/json',
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      ...headers,
    },
    signal,
    credentials: 'include',
  };
  if (body !== undefined) init.body = JSON.stringify(body);

  const response = await fetch(buildUrl(path), init);
  const envelope = await parseEnvelope<T>(response);

  if (!response.ok) {
    throw new ApiError(
      `Request failed: ${response.status} ${response.statusText}`,
      response.status,
      envelope?.code ?? -1,
      envelope?.message ?? response.statusText,
      envelope
    );
  }

  if (!envelope) {
    throw new ApiError(
      'Response did not contain a JSON envelope',
      response.status,
      -1,
      'malformed response'
    );
  }

  if (envelope.code !== 0) {
    throw new ApiError(
      `API error (code=${envelope.code}): ${envelope.message}`,
      response.status,
      envelope.code,
      envelope.message,
      envelope
    );
  }

  return envelope.data;
}

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  delete: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'DELETE' }),
};

export default api;
